import "./menu.css";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../theme-context";
import { useLocale } from "../../locale-context";
import { AppLink, localizedPath } from "../../router";
import { themeNames } from "../../i18n";

const pages = [
  ["about", "/about/"],
  ["skills", "/skills/"],
  ["projects", "/projects/"],
  ["journal", "/journal/"],
  ["contact", "/contact/"],
];

export default function Menu({ activePage }) {
  const { theme, toggleTheme } = useTheme();
  const { locale, setLocale, t } = useLocale();
  const [open, setOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const languageRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (!languageRef.current?.contains(e.target)) setLanguageOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);
  const languageKeys = (e) => {
    if (e.key === "Escape") {
      setLanguageOpen(false);
      languageRef.current?.querySelector(".language-trigger")?.focus();
    }
    if (["ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      setLanguageOpen(true);
      const values = ["EN", "ES", "ET"],
        direction = e.key === "ArrowDown" ? 1 : -1;
      setLocale(
        values[
        (values.indexOf(locale) + direction + values.length) % values.length
        ],
      );
    }
  };
  return (
    <header className="site-header">
      <AppLink
        className="wordmark"
        href={localizedPath(locale, "/")}
        aria-label={`RB. ${t.nav[0]}`}
      >
        RB<span>.</span>
      </AppLink>
      <nav
        className={open ? "site-nav open" : "site-nav"}
        aria-label={t.menu}
      >
        {pages.map(([page, path], index) => (
          <AppLink
            key={page}
            href={localizedPath(locale, path)}
            onClick={() => setOpen(false)}
            className={activePage === page ? "active" : ""}
          >
            {t.nav[index]}
          </AppLink>
        ))}
      </nav>
      <div className="header-actions">
        <div
          className="language-menu"
          ref={languageRef}
          onKeyDown={languageKeys}
        >
          <button
            className="language-trigger"
            aria-label={locale}
            aria-haspopup="menu"
            aria-expanded={languageOpen}
            onClick={() => setLanguageOpen(!languageOpen)}
          >
            {locale}
            <span aria-hidden="true">{languageOpen ? "↑" : "↓"}</span>
          </button>
          {languageOpen && (
            <div className="language-options" role="menu">
              {["EN", "ES", "ET"].map((code) => (
                <button
                  role="menuitemradio"
                  aria-checked={locale === code}
                  className={locale === code ? "active" : ""}
                  onClick={() => {
                    setLocale(code);
                    setLanguageOpen(false);
                  }}
                  key={code}
                >
                  {code}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={t.theme}
        >
          <span>{theme === "light" ? "●" : "○"}</span>
          {theme === "light" ? themeNames[locale].dark : themeNames[locale].light}
        </button>
        <button
          className={open ? "menu-toggle active" : "menu-toggle"}
          onClick={() => setOpen(!open)}
          aria-label={open ? t.closeMenu : t.menu}
          aria-expanded={open}
        >
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
}
